import type { LandmarkName, TuroPose } from "./turo-world-poses";

/**
 * Turo World Level 2 — "My Body, My Hello". Nineteen poses.
 *
 * 🔴 EVERY POSE HERE IS `calibrationQuality: "low"`, AND THAT IS A SAFETY
 * DECISION RATHER THAN A MEASUREMENT ONE. `TURO_WORLD_L2_L3_PLAN.md` §3.1: *"L2
 * must not touch `kalqy.profile.bodyScale` — folded arms and self-hug occlude
 * the torso and the measurements are bad."* Marking them low is belt-and-braces
 * under the level gate on the write itself: two independent reasons this level
 * cannot corrupt a child's calibration, so removing either one alone is not
 * enough to do harm.
 *
 * 🔴 SIXTEEN OF THESE COULD NOT BE EXPRESSED BEFORE `displacements.ts`. The five
 * angle measures answer *"how bent is this joint"*; `arms_folded` and `self_hug`
 * have near-identical elbow and shoulder angles and differ only in where the
 * wrists ended up. See plan C-2.
 *
 * ⚠️ FOUR POSES ARE `graded: false`, IN TWO DIFFERENT SENSES, AND THE COMMENTS
 * SAY WHICH:
 *   • `handshake` and `thumb_to_chest` are UNGRADEABLE — plan §5 lists them
 *     under "not checked": a handshake is depth-ambiguous on a single front
 *     lens, and a thumb to the chest needs hand landmarks this kit never reads.
 *   • the four WATCH scenes are NOT MEANT TO BE COPIED — Turo demonstrates them
 *     and the child watches, so there is no attempt to grade and no ghost to
 *     show (asset pack §9, `silhouette: null`).
 *
 * ⚠️ NO TOLERANCE, NO DURATION, NO DIFFICULTY appears in this file.
 * `check-game-contract.mjs` fails a game declaring a threshold, and ADR-031 is
 * why: *"a level-5 T-pose is the same T-pose as a level-1 T-pose."* A pose table
 * says WHICH JOINTS AND WHAT VALUE. How close counts as close enough belongs to
 * `poseMatch.ts`.
 */

/** Shoulders and hips — every displacement divides by the torso they define. */
const TRUNK: LandmarkName[] = [
  "leftShoulder",
  "rightShoulder",
  "leftHip",
  "rightHip",
];
const ARMS: LandmarkName[] = [
  ...TRUNK,
  "leftElbow",
  "rightElbow",
  "leftWrist",
  "rightWrist",
];

/** A pose that is shown and copied but never graded — see the header. */
const watch = (
  id: string,
  cue: string,
  concept: string,
): TuroPose => ({
  id,
  frame: "full",
  graded: false,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: [],
  targets: [],
  audio: { cue: `vo_turo_${id}_cue` },
  cue: { en: cue },
  hint: { en: concept },
  sprite: id,
  /**
   * 🔴 THE GHOST IS THE POSE'S OWN ID EVERYWHERE ELSE; HERE IT IS EMPTY.
   * A WATCH beat has nothing for the child to copy — showing a silhouette to
   * step into would invite them to imitate an adult hugging a child, which is
   * the one thing this level must never ask for.
   */
  ghost: "",
});

/* ────────────────────────────────────── S1 · Hello my way ── */

const WAVE_HELLO: TuroPose = {
  id: "wave_hello",
  frame: "upper",
  graded: true,
  /**
   * ⚠️ A HOLD, NOT A REPETITION — plan C-3. §5.1 calls the motion pose class the
   * main new engineering cost and then offers its own fallback: *"grade the wave
   * as a static hand-up-beside-the-head hold and let the motion be cosmetic."*
   * L1 already shipped exactly that for `wave`, so the cost is paid.
   */
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  mirrorTolerant: true,
  requiredLandmarks: ARMS,
  targets: [
    { measure: "elbow", side: "left", value: 105 },
    { measure: "shoulderAbduction", side: "left", value: 120 },
  ],
  audio: { cue: "vo_turo_greet_wave_cue" },
  cue: { en: "Wave hello! Hello, hello!" },
  sprite: "wave_hello",
  ghost: "wave_hello",
};

const NAMASTE: TuroPose = {
  id: "namaste",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: ARMS,
  targets: [
    /* Palms together at the chest — plan §5's "<0.2T apart, chest height". */
    { measure: "wristSeparation", side: "left", value: 0.14, op: "lte" },
    { measure: "wristHeightInTorso", side: "left", value: 0.3, op: "lte" },
    /* ⚠️ ELBOWS OUT is what separates a namaste from praying hands held close;
     * without it a child with elbows pinned to their sides also passes. */
    { measure: "shoulderAbduction", side: "left", value: 45 },
  ],
  audio: { cue: "vo_turo_greet_namaste_cue" },
  cue: { en: "Hands together. Namaste!" },
  sprite: "namaste",
  ghost: "namaste",
};

const HIGH_FIVE: TuroPose = {
  id: "high_five",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  mirrorTolerant: true,
  requiredLandmarks: ARMS,
  targets: [
    { measure: "shoulderAbduction", side: "left", value: 155 },
    { measure: "elbow", side: "left", value: 160 },
  ],
  audio: { cue: "vo_turo_greet_highfive_cue" },
  cue: { en: "Up high! High five!" },
  sprite: "high_five",
  ghost: "high_five",
};

const HANDSHAKE: TuroPose = {
  id: "handshake",
  frame: "upper",
  /**
   * 🔴 NOT GRADED — plan §5 lists it under "not checked": *"depth-ambiguous on a
   * single front lens."* An arm reaching TOWARD the camera and an arm hanging
   * down project to nearly the same picture, and a pose that cannot be told from
   * a resting arm would pass for a child who did nothing.
   */
  graded: false,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  mirrorTolerant: true,
  requiredLandmarks: [],
  targets: [],
  audio: { cue: "vo_turo_greet_handshake_cue" },
  cue: { en: "Put your hand out to say hello." },
  sprite: "handshake",
  ghost: "handshake",
};

const OPEN_ARMS: TuroPose = {
  id: "open_arms",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: ARMS,
  targets: [
    { measure: "shoulderAbduction", side: "left", value: 55 },
    { measure: "shoulderAbduction", side: "right", value: 55 },
    { measure: "elbow", side: "left", value: 150 },
    { measure: "elbow", side: "right", value: 150 },
  ],
  audio: { cue: "vo_turo_greet_hug_cue" },
  cue: { en: "Open your arms wide, ready for a hug." },
  sprite: "open_arms",
  ghost: "open_arms",
};

/* ─────────────────────────────────── S2 · My body is mine ── */

const HANDS_ON_HIPS: TuroPose = {
  id: "hands_on_hips",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: ARMS,
  targets: [
    { measure: "wristHeightInTorso", side: "left", value: 0.95 },
    { measure: "wristHeightInTorso", side: "right", value: 0.95 },
    { measure: "elbow", side: "left", value: 95 },
    { measure: "elbow", side: "right", value: 95 },
  ],
  audio: { cue: "vo_turo_body_boss_cue" },
  cue: { en: "Hands on your hips!" },
  sprite: "hands_on_hips",
  ghost: "hands_on_hips",
};

const ARMS_FOLDED: TuroPose = {
  id: "arms_folded",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: ARMS,
  targets: [
    /*
     * 🔴 THE SEPARATOR FROM `self_hug`, AND THE ONLY ONE THERE IS. Both poses
     * cross the arms high over the chest with near-identical elbow angles. Plan
     * §5: folded arms tuck the hands UNDER the elbows (wrists low); the self-hug
     * rests them ON the shoulders (wrists high). `wristAboveElbow` is signed, so
     * `lte` a negative value means "wrist below elbow" without a boolean that
     * could strobe the hold timer at the boundary.
     */
    { measure: "wristAboveElbow", side: "left", value: -0.04, op: "lte" },
    { measure: "wristAboveElbow", side: "right", value: -0.04, op: "lte" },
    { measure: "wristMidlineOffset", side: "left", value: 0.04, op: "gte" },
    { measure: "wristMidlineOffset", side: "right", value: 0.04, op: "gte" },
  ],
  audio: { cue: "vo_turo_body_mine_cue" },
  cue: { en: "Fold your arms like this." },
  sprite: "arms_folded",
  ghost: "arms_folded",
};

const SELF_HUG: TuroPose = {
  id: "self_hug",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: ARMS,
  targets: [
    /* Hands ON the opposite shoulders — plan §5's "within 0.4T". */
    { measure: "wristToOppositeShoulder", side: "left", value: 0.4, op: "lte" },
    { measure: "wristToOppositeShoulder", side: "right", value: 0.4, op: "lte" },
    /* …and HIGH, which is what `arms_folded` is not. */
    { measure: "wristAboveElbow", side: "left", value: 0.04, op: "gte" },
    { measure: "wristAboveElbow", side: "right", value: 0.04, op: "gte" },
  ],
  audio: { cue: "vo_turo_body_selfhug_cue" },
  cue: { en: "Give yourself a big hug." },
  sprite: "self_hug",
  ghost: "self_hug",
};

const THUMB_TO_CHEST: TuroPose = {
  id: "thumb_to_chest",
  frame: "upper",
  /**
   * 🔴 NOT GRADED — plan §5, "not checked": it *"needs hand landmarks"*, and
   * this kit reads pose landmarks only. A thumb and an index finger are the same
   * wrist to MediaPipe's pose model.
   */
  graded: false,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  mirrorTolerant: true,
  requiredLandmarks: [],
  targets: [],
  audio: { cue: "vo_turo_body_thatsme_cue" },
  cue: { en: "Point at yourself!" },
  sprite: "thumb_to_chest",
  ghost: "thumb_to_chest",
};

/* ────────────────────────────── S3 · Safe touches, and no ── */

const TWO_THUMBS_UP: TuroPose = {
  id: "two_thumbs_up",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: ARMS,
  targets: [
    /*
     * ⚠️ GRADE THE FOREARMS, NOT THE THUMBS — plan §5 says so explicitly, for
     * the same reason `thumb_to_chest` is ungraded: the thumb is invisible to a
     * pose model. Elbows tucked and hands at chest height is the whole test.
     */
    { measure: "elbow", side: "left", value: 85 },
    { measure: "elbow", side: "right", value: 85 },
    { measure: "wristHeightInTorso", side: "left", value: 0.25, op: "lte" },
    { measure: "wristHeightInTorso", side: "right", value: 0.25, op: "lte" },
  ],
  audio: { cue: "vo_turo_body_thumbs_cue" },
  cue: { en: "Two thumbs up!" },
  sprite: "two_thumbs_up",
  ghost: "two_thumbs_up",
};

const NO_THANK_YOU: TuroPose = {
  id: "no_thank_you",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  /** Either hand may be the raised one — plan §5, "either hand". */
  mirrorTolerant: true,
  requiredLandmarks: ARMS,
  targets: [
    /*
     * 🔴 THE RAISED OPEN PALM IS THE POSE. The art reroll that lost it came back
     * with both hands flat on the chest, which IS `hands_on_heart` — a different
     * pose in S4. The raised hand is held out from the body and away from the
     * midline; the other rests over the heart.
     */
    { measure: "wristHeightInTorso", side: "left", value: 0.3, op: "lte" },
    { measure: "wristMidlineOffset", side: "left", value: -0.1, op: "lte" },
    { measure: "wristToOppositeShoulder", side: "right", value: 0.55, op: "lte" },
  ],
  audio: { cue: "vo_turo_body_nothankyou_cue" },
  cue: { en: "One hand up. No thank you!" },
  sprite: "no_thank_you",
  ghost: "no_thank_you",
};

const SAY_NO_CALM: TuroPose = {
  id: "say_no_calm",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  /** Symmetric — plan §5: "no mirroring case". */
  mirrorTolerant: false,
  requiredLandmarks: ARMS,
  targets: [
    /* Both palms forward at shoulder height, arms out — plan §5. */
    { measure: "wristHeightInTorso", side: "left", value: 0.15, op: "lte" },
    { measure: "wristHeightInTorso", side: "right", value: 0.15, op: "lte" },
    { measure: "elbow", side: "left", value: 140 },
    { measure: "elbow", side: "right", value: 140 },
  ],
  audio: { cue: "vo_turo_body_no_cue" },
  cue: { en: "Both hands up. No!" },
  sprite: "say_no_calm",
  ghost: "say_no_calm",
};

/* ────────────────────────── S4 · My tummy and my people ── */

const HANDS_ON_TUMMY: TuroPose = {
  id: "hands_on_tummy",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: ARMS,
  targets: [
    { measure: "wristSeparation", side: "left", value: 0.4, op: "lte" },
    /*
     * 🔴 LOW ON THE TORSO — THE ONLY THING SEPARATING THIS FROM `hands_on_heart`.
     * Plan §5 calls them a confusable pair and asset pack §4 tells the artist to
     * "exaggerate it" for the same reason. Both hands together on the torso is
     * identical in every angle; only the height differs.
     */
    { measure: "wristHeightInTorso", side: "left", value: 0.65, op: "gte" },
    { measure: "wristHeightInTorso", side: "right", value: 0.65, op: "gte" },
  ],
  audio: { cue: "vo_turo_body_uhoh_cue" },
  cue: { en: "Put your hands on your tummy." },
  sprite: "hands_on_tummy",
  ghost: "hands_on_tummy",
};

const HANDS_ON_HEART: TuroPose = {
  id: "hands_on_heart",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: ARMS,
  targets: [
    { measure: "wristSeparation", side: "left", value: 0.4, op: "lte" },
    /* HIGH — the other half of the pair above. */
    { measure: "wristHeightInTorso", side: "left", value: 0.35, op: "lte" },
    { measure: "wristHeightInTorso", side: "right", value: 0.35, op: "lte" },
  ],
  audio: { cue: "vo_turo_body_safe_cue" },
  cue: { en: "Hands on your heart." },
  sprite: "hands_on_heart",
  ghost: "hands_on_heart",
};

const FIVE_PEOPLE: TuroPose = {
  id: "five_people",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 2,
  calibrationQuality: "low",
  mirrorTolerant: true,
  requiredLandmarks: ARMS,
  targets: [
    /*
     * ⚠️ GRADE THE RAISED ARM ONLY — plan §5 says so in as many words: *"spread
     * fingers are cosmetic, a raised fist passes."* Fingers are invisible to a
     * pose model, and a child counting on four fingers has still counted their
     * five people.
     */
    { measure: "shoulderAbduction", side: "left", value: 150 },
    { measure: "wristHeightInTorso", side: "left", value: -0.3, op: "lte" },
  ],
  audio: { cue: "vo_turo_body_five_cue" },
  cue: { en: "Hold up five fingers!" },
  sprite: "five_people",
  ghost: "five_people",
};

export const L2_POSES: readonly TuroPose[] = [
  WAVE_HELLO, NAMASTE, HIGH_FIVE, HANDSHAKE, OPEN_ARMS,
  HANDS_ON_HIPS, ARMS_FOLDED, SELF_HUG, THUMB_TO_CHEST,
  TWO_THUMBS_UP,
  watch("hug_mother", "Watch this one.", "A hug from someone I love feels warm."),
  watch("holding_hands", "Watch this one.", "Holding hands keeps me safe."),
  watch("teacher_welldone", "Watch this one.", "Well done! That feels good too."),
  NO_THANK_YOU, SAY_NO_CALM,
  HANDS_ON_TUMMY, HANDS_ON_HEART, FIVE_PEOPLE,
  watch("tell_adult", "Watch this one.", "And I tell. Telling is always the right thing to do."),
];

export const l2PoseById = (id: string): TuroPose | undefined =>
  L2_POSES.find((p) => p.id === id);
